import { ClientUser, driverStatusFingerprint, DriverStatus, DriverStatusDelta, DriverStatusMutationResponse } from '@numeo/types-core'
import { createAction, PayloadAction } from '@reduxjs/toolkit'

export const patchDriverStatus = createAction<DriverStatusMutationResponse>('auth/patchDriverStatus')

/** Accept a legacy server during rollout, but retain only the requested driver's history. */
export const getDriverStatusMutation = (
    response: DriverStatusMutationResponse | { users: Pick<ClientUser, 'id' | 'driverStatus'>[] },
    driverId: string
): DriverStatusMutationResponse =>
    'driverId' in response ? response : { driverId, driverStatus: response.users.find((user) => user.id === driverId)?.driverStatus ?? [] }

export const patchDriverStatusDelta = createAction<DriverStatusDelta>('auth/patchDriverStatusDelta')

export const mergeDriverStatusDelta = (history: DriverStatus[] = [], delta: DriverStatusDelta): DriverStatus[] => {
    const removed = new Set(delta.deletedEntryIds)
    const upserts = new Map(delta.upserts.map((status) => [String(status._id), status]))
    return [...history.filter((status) => !removed.has(String(status._id)) && !upserts.has(String(status._id))), ...upserts.values()].sort(
        (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
    )
}

type DriverPatch = Pick<ClientUser, 'id' | 'driverStatus'>
type HistoryMutation = DriverStatusMutationResponse | DriverStatusDelta
const mergeHistory = (history: DriverStatus[] | undefined, change: HistoryMutation): DriverStatus[] =>
    'driverStatus' in change ? change.driverStatus : mergeDriverStatusDelta(history, change)

const patchDrivers = (drivers: DriverPatch[] | undefined, change: HistoryMutation): void => {
    for (const driver of drivers ?? []) if (driver.id === change.driverId) driver.driverStatus = mergeHistory(driver.driverStatus, change)
}

export const patchDriverStatusReducer = (state: { application: { users: DriverPatch[] } | null }, action: PayloadAction<HistoryMutation>) =>
    patchDrivers(state.application?.users, action.payload)

export const patchSchedulerDriverStatusReducer = (state: { processedDrivers?: { drivers?: DriverPatch[] }[] }, action: PayloadAction<HistoryMutation>) => {
    for (const row of state.processedDrivers ?? []) patchDrivers(row.drivers, action.payload)
}

/**
 * A driver as the board's own rows carry them (`GET /v1/updater/loads/processed-drivers`: name, phone, email and the
 * `driverStatus` history for the window), or undefined when no row on this page holds them. The board reads a driver
 * here, never from `/auth/verify`'s roster: the rows are what the status patches above keep current.
 */
export const findSchedulerDriver = <T extends { id?: string }>(rows: readonly { drivers?: readonly T[] }[] | undefined, driverId: unknown): T | undefined => {
    if (driverId === undefined || driverId === null || driverId === '') return undefined
    const id = String(driverId)
    for (const row of rows ?? []) for (const driver of row.drivers ?? []) if (driver?.id !== undefined && String(driver.id) === id) return driver
    return undefined
}

/** A matching HTTP response may already have applied this socket echo. */
export const driverStatusDeltaChanges = (history: DriverStatus[] | undefined, delta: DriverStatusDelta): boolean => {
    if (!history) return delta.upserts.length > 0 || delta.deletedEntryIds.length > 0
    const previous = new Map(history.map((status) => [String(status._id), status]))
    return (
        delta.deletedEntryIds.some((id) => previous.has(id)) ||
        delta.upserts.some((status) => {
            const current = previous.get(String(status._id))
            return !current || driverStatusFingerprint(current) !== driverStatusFingerprint(status)
        })
    )
}
